/**
 * VariantConfigurator Component
 * Simple UI for managing product variants (sizes and colors)
 */

import React, { useState, useEffect, useRef } from 'react';
import './VariantConfigurator.css';

const VariantConfigurator = ({ productData, onChange }) => {
    const isInitialized = useRef(false);
    const [hasVariants, setHasVariants] = useState(false);
    const [variantTypes, setVariantTypes] = useState({ size: false, color: false });
    const [sizes, setSizes] = useState([]);
    const [colors, setColors] = useState([]);
    const [combinations, setCombinations] = useState([]);
    const [newSize, setNewSize] = useState('');
    const [newColor, setNewColor] = useState('');

    // Common size options
    const commonSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL'];

    // Common color options
    const commonColors = [
        { name: 'Black', hex: '#000000' },
        { name: 'White', hex: '#FFFFFF' },
        { name: 'Red', hex: '#FF0000' },
        { name: 'Blue', hex: '#0000FF' },
        { name: 'Green', hex: '#008000' },
        { name: 'Navy', hex: '#000080' },
        { name: 'Gray', hex: '#808080' },
        { name: 'Maroon', hex: '#800000' }
    ];

    // Load existing variant data
    useEffect(() => {
        if (productData?.variants_data) {
            try {
                const data = JSON.parse(productData.variants_data);
                setHasVariants(true);
                setSizes(data.sizes || []);
                setColors(data.colors || []);
                setCombinations(data.combinations || []);

                // Load active types if available, otherwise fallback to checking array length
                if (data.activeTypes) {
                    setVariantTypes(data.activeTypes);
                } else {
                    setVariantTypes({
                        size: data.sizes && data.sizes.length > 0,
                        color: data.colors && data.colors.length > 0
                    });
                }
            } catch (e) {
                console.error('Error parsing variants:', e);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productData?.id, productData?.variants_data]); // Only reload when product ID changes to prevent infinite loops

    // Generate combinations when sizes or colors change
    useEffect(() => {
        if (!hasVariants) return;

        const newCombinations = [];

        if (variantTypes.size && variantTypes.color) {
            // Both size and color
            sizes.forEach(size => {
                colors.forEach(color => {
                    const existing = combinations.find(c => c.size === size && c.color === color.name);
                    newCombinations.push({
                        size,
                        color: color.name,
                        colorHex: color.hex,
                        sku: existing?.sku || '',
                        inventory: existing?.inventory || 0,
                        priceAdjustment: existing?.priceAdjustment || 0
                    });
                });
            });
        } else if (variantTypes.size) {
            // Size only
            sizes.forEach(size => {
                const existing = combinations.find(c => c.size === size);
                newCombinations.push({
                    size,
                    sku: existing?.sku || '',
                    inventory: existing?.inventory || 0,
                    priceAdjustment: existing?.priceAdjustment || 0
                });
            });
        } else if (variantTypes.color) {
            // Color only
            colors.forEach(color => {
                const existing = combinations.find(c => c.color === color.name);
                newCombinations.push({
                    color: color.name,
                    colorHex: color.hex,
                    sku: existing?.sku || '',
                    inventory: existing?.inventory || 0,
                    priceAdjustment: existing?.priceAdjustment || 0
                });
            });
        }

        setCombinations(newCombinations);
    }, [sizes, colors, variantTypes, hasVariants]); // eslint-disable-line react-hooks/exhaustive-deps

    // Notify parent of changes (skip on initial render)
    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;
            return;
        }

        if (onChange) {
            const variantData = hasVariants ? {
                sizes,
                colors,
                combinations,
                activeTypes: variantTypes
            } : null;

            onChange({
                has_variants: hasVariants ? 1 : 0,
                variant_type: variantTypes.size && variantTypes.color ? 'size_color' :
                    variantTypes.size ? 'size' :
                        variantTypes.color ? 'color' : null,
                variants_data: variantData ? JSON.stringify(variantData) : null
            });
        }
    }, [hasVariants, sizes, colors, combinations, variantTypes]); // eslint-disable-line react-hooks/exhaustive-deps

    const addSize = (size) => {
        if (size && !sizes.includes(size)) {
            setSizes([...sizes, size]);
            setNewSize('');
        }
    };

    const removeSize = (size) => {
        setSizes(sizes.filter(s => s !== size));
    };

    const addColor = (colorName, colorHex) => {
        if (colorName && !colors.find(c => c.name === colorName)) {
            setColors([...colors, { name: colorName, hex: colorHex || '#000000' }]);
            setNewColor('');
        }
    };

    const removeColor = (colorName) => {
        setColors(colors.filter(c => c.name !== colorName));
    };

    const updateCombination = (index, field, value) => {
        const updated = [...combinations];
        updated[index] = { ...updated[index], [field]: value };
        setCombinations(updated);
    };

    if (!hasVariants) {
        return (
            <div className="variant-configurator">
                <div className="variant-toggle">
                    <label>
                        <input
                            type="checkbox"
                            checked={hasVariants}
                            onChange={(e) => setHasVariants(e.target.checked)}
                        />
                        <span>This product has variants (sizes, colors, etc.)</span>
                    </label>
                </div>
            </div>
        );
    }

    return (
        <div className="variant-configurator active">
            <div className="variant-toggle">
                <label>
                    <input
                        type="checkbox"
                        checked={hasVariants}
                        onChange={(e) => setHasVariants(e.target.checked)}
                    />
                    <span>This product has variants (sizes, colors, etc.)</span>
                </label>
            </div>

            <div className="variant-types">
                <h4>Variant Types:</h4>
                <label>
                    <input
                        type="checkbox"
                        checked={variantTypes.size}
                        onChange={(e) => setVariantTypes({ ...variantTypes, size: e.target.checked })}
                    />
                    <span>Sizes</span>
                </label>
                <label>
                    <input
                        type="checkbox"
                        checked={variantTypes.color}
                        onChange={(e) => setVariantTypes({ ...variantTypes, color: e.target.checked })}
                    />
                    <span>Colors</span>
                </label>
            </div>

            {variantTypes.size && (
                <div className="variant-section">
                    <h4>Available Sizes:</h4>
                    <div className="size-chips">
                        {sizes.map(size => (
                            <div key={size} className="chip">
                                {size}
                                <button onClick={() => removeSize(size)}>✕</button>
                            </div>
                        ))}
                    </div>
                    <div className="add-size">
                        <select
                            value={newSize}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val) addSize(val);
                            }}
                        >
                            <option value="">-- Select Size --</option>
                            {commonSizes.map(size => (
                                <option key={size} value={size} disabled={sizes.includes(size)}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {variantTypes.color && (
                <div className="variant-section">
                    <h4>Available Colors:</h4>
                    <div className="color-chips">
                        {colors.map(color => (
                            <div key={color.name} className="chip color-chip">
                                <span className="color-dot" style={{ backgroundColor: color.hex }}></span>
                                {color.name}
                                <button onClick={() => removeColor(color.name)}>✕</button>
                            </div>
                        ))}
                    </div>
                    <div className="add-color">
                        <select
                            value={newColor}
                            onChange={(e) => {
                                const selected = commonColors.find(c => c.name === e.target.value);
                                if (selected) {
                                    addColor(selected.name, selected.hex);
                                }
                            }}
                        >
                            <option value="">-- Select Color --</option>
                            {commonColors.map(color => (
                                <option key={color.name} value={color.name} disabled={colors.find(c => c.name === color.name)}>
                                    {color.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {combinations.length > 0 && (
                <div className="variant-combinations">
                    <h4>Variant Combinations ({combinations.length}):</h4>
                    <div className="combinations-table">
                        <table>
                            <thead>
                                <tr>
                                    {variantTypes.size && <th>Size</th>}
                                    {variantTypes.color && <th>Color</th>}
                                    <th>SKU Suffix</th>
                                    <th>Stock</th>
                                    <th>Price +/-</th>
                                </tr>
                            </thead>
                            <tbody>
                                {combinations.map((combo, index) => (
                                    <tr key={index}>
                                        {variantTypes.size && <td><strong>{combo.size}</strong></td>}
                                        {variantTypes.color && (
                                            <td>
                                                <span className="color-indicator">
                                                    <span className="color-dot" style={{ backgroundColor: combo.colorHex }}></span>
                                                    {combo.color}
                                                </span>
                                            </td>
                                        )}
                                        <td>
                                            <input
                                                type="text"
                                                value={combo.sku}
                                                onChange={(e) => updateCombination(index, 'sku', e.target.value)}
                                                placeholder="e.g. -S-RED"
                                                style={{ width: '100px', fontSize: '12px' }}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                value={combo.inventory}
                                                onChange={(e) => updateCombination(index, 'inventory', parseInt(e.target.value) || 0)}
                                                min="0"
                                                style={{ width: '60px', fontSize: '12px' }}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                value={combo.priceAdjustment}
                                                onChange={(e) => updateCombination(index, 'priceAdjustment', parseFloat(e.target.value) || 0)}
                                                step="0.01"
                                                placeholder="0.00"
                                                style={{ width: '70px', fontSize: '12px' }}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="variant-help">
                        💡 Leave SKU empty to use parent product SKU. Price adjustment is added to base price (use negative for discounts).
                    </p>
                </div>
            )}
        </div>
    );
};

export default VariantConfigurator;
